import { useState, useEffect, useCallback, useRef } from "react";
import { api, NODE_TYPE_COLORS } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { toast } from "sonner";
import {
  Lock, Unlock, Trash2, Edit3, Plus, Minus, Check, Zap,
  GitBranch, Bell, StickyNote, Box, Archive,
} from "lucide-react";

// ─── Constants ────────────────────────────────────────────────────────────────
const NODE_W = 200, NODE_H = 78;
const ADD_TYPES = [
  { type: "trigger",   icon: Zap,        color: "#FF8A00" },
  { type: "action",    icon: Box,        color: "#0055FF" },
  { type: "condition", icon: GitBranch,  color: "#A855F7" },
  { type: "task",      icon: Check,      color: "#EF4444" },
  { type: "reminder",  icon: Bell,       color: "#00C781" },
  { type: "note",      icon: StickyNote, color: "#A1A1AA" },
];
const COLORS = ["#5A90F5","#FF8A00","#A855F7","#00C781","#EF4444","#14B8A6","#F59E0B","#6366F1"];

function isLocked(node) {
  return node.status === "blocked" && Array.isArray(node.depends_on) && node.depends_on.length > 0;
}

// ─── Automation board form (create/edit) ────────────────────────────────────
function AutomationForm({ initial, onClose, onSave }) {
  const [name, setName] = useState(initial?.name || "");
  const [desc, setDesc] = useState(initial?.description || "");
  const [color, setColor] = useState(initial?.color || "#5A90F5");
  return (
    <div className="fixed inset-0 z-[500] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#141414] border border-[#2A2A2A] rounded-2xl p-6 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-[15px] font-semibold text-[#EDEDED] mb-4">{initial ? "Edit" : "New"} automation</h3>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Automation name..."
          className="w-full bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-[13px] text-[#EDEDED] placeholder-[#71717A] outline-none focus:border-[#5A90F5] mb-3" />
        <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description (optional)..."
          className="w-full bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-[12px] text-[#EDEDED] placeholder-[#71717A] outline-none resize-none mb-4" rows={2} />
        <div className="flex gap-1.5 mb-5">
          {COLORS.map(c => (
            <button key={c} onClick={() => setColor(c)} className="w-6 h-6 rounded-full border-2 transition-all"
              style={{ background: c, borderColor: color === c ? "#fff" : "transparent" }} />
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-[12px] border border-[#2A2A2A] text-[#A1A1AA]">Cancel</button>
          <button onClick={() => { if (name.trim()) onSave({ name: name.trim(), description: desc, color }); }} disabled={!name.trim()}
            className="flex-1 py-2.5 rounded-xl text-[12px] font-semibold text-white disabled:opacity-30 transition-colors" style={{ background: color }}>
            {initial ? "Save" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Node edit dialog (lightweight) ─────────────────────────────────────────
function NodeDialog({ node, allNodes, onClose, onSave, onDelete }) {
  const [title, setTitle] = useState(node.title || "");
  const [description, setDescription] = useState(node.description || "");
  const [dependsOn, setDependsOn] = useState(node.depends_on || []);
  const color = NODE_TYPE_COLORS[node.type] || "#5A90F5";

  const toggleDep = (id) => setDependsOn(d => d.includes(id) ? d.filter(x => x !== id) : [...d, id]);
  const candidates = allNodes.filter(n => n.event_id !== node.event_id);

  return (
    <div className="fixed inset-0 z-[500] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#141414] border border-[#2A2A2A] rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2 h-2 rounded-full" style={{ background: color }} />
          <span className="text-[10px] uppercase tracking-wider text-[#A1A1AA]">{node.type}</span>
        </div>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Node title..."
          className="w-full bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-[13px] text-[#EDEDED] placeholder-[#71717A] outline-none focus:border-[#5A90F5] mb-3" />
        <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Description..."
          className="w-full bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-[12px] text-[#EDEDED] placeholder-[#71717A] outline-none resize-none mb-4" rows={2} />

        <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-2">Depends on (locks until complete)</p>
        <div className="max-h-40 overflow-y-auto space-y-1 mb-4 border border-[#2A2A2A] rounded-xl p-2">
          {candidates.length === 0 ? (
            <p className="text-[11px] text-[#71717A] px-1 py-2">No other nodes in this automation</p>
          ) : candidates.map(n => (
            <label key={n.event_id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[#1A1A1A] cursor-pointer">
              <input type="checkbox" checked={dependsOn.includes(n.event_id)} onChange={() => toggleDep(n.event_id)} className="accent-[#5A90F5]" />
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: NODE_TYPE_COLORS[n.type] || "#5A90F5" }} />
              <span className="text-[11px] text-[#EDEDED] truncate">{n.title || n.type}</span>
            </label>
          ))}
        </div>

        <div className="flex gap-2">
          <button onClick={() => { onDelete(node.event_id); onClose(); }}
            className="p-2.5 rounded-xl border border-[#EF4444]/20 text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-[12px] border border-[#2A2A2A] text-[#A1A1AA]">Cancel</button>
          <button onClick={() => { onSave(node.event_id, { title: title.trim() || node.type, description, depends_on: dependsOn, status: dependsOn.length > 0 ? "blocked" : "todo" }); onClose(); }}
            className="flex-1 py-2.5 rounded-xl text-[12px] font-semibold text-white transition-colors" style={{ background: color }}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Canvas — a single automation's workflow ────────────────────────────────
function AutomationCanvas({ automation, wsId, onNodeCountChange }) {
  const canvasRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [edges, setEdges] = useState([]);
  const [pan, setPan] = useState({ x: 40, y: 40 });
  const [scale, setScale] = useState(0.85);
  const [dragging, setDragging] = useState(null);
  const [positions, setPositions] = useState({});
  const [connectSrc, setConnectSrc] = useState(null);
  const [cursorPos, setCursorPos] = useState(null);
  const [editingNode, setEditingNode] = useState(null);
  const [ctxMenu, setCtxMenu] = useState(null);
  const panStart = useRef(null);

  const automationId = automation.automation_id;

  const load = useCallback(async () => {
    if (!wsId || !automationId) return;
    try {
      const [evRes, edgeRes] = await Promise.all([
        api.get(`/workspaces/${wsId}/events`),
        api.get(`/workspaces/${wsId}/edges`),
      ]);
      const nodes = (evRes.data || []).filter(e => e.automation_id === automationId);
      const nodeIds = new Set(nodes.map(n => n.event_id));
      setEvents(nodes);
      setEdges((edgeRes.data || []).filter(e => nodeIds.has(e.source) && nodeIds.has(e.target)));
      onNodeCountChange?.(automationId, nodes.length);
    } catch {}
  }, [wsId, automationId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setPositions(prev => {
      const pos = { ...prev };
      const groups = {};
      events.forEach(ev => { if (!groups[ev.type]) groups[ev.type] = []; groups[ev.type].push(ev); });
      const typeOrder = ["trigger", "condition", "action", "task", "reminder", "note"];
      let colX = 60;
      typeOrder.concat(Object.keys(groups).filter(t => !typeOrder.includes(t))).forEach(type => {
        if (!groups[type]) return;
        groups[type].forEach((ev, i) => { if (!pos[ev.event_id]) pos[ev.event_id] = { x: ev.position_x ?? colX, y: ev.position_y ?? 60 + i * 110 }; });
        colX += 240;
      });
      return pos;
    });
  }, [events]);

  const toCanvas = (cx, cy) => {
    const r = canvasRef.current?.getBoundingClientRect() || { left: 0, top: 0 };
    return { x: (cx - r.left - pan.x) / scale, y: (cy - r.top - pan.y) / scale };
  };

  const onCanvasPointerDown = e => {
    if (!e.target.closest("[data-conn-port]") && !e.target.closest("[data-node-card]")) {
      panStart.current = { mx: e.clientX, my: e.clientY, px: pan.x, py: pan.y };
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    setCtxMenu(null);
  };
  const onCanvasPointerMove = e => {
    if (panStart.current) setPan({ x: panStart.current.px + e.clientX - panStart.current.mx, y: panStart.current.py + e.clientY - panStart.current.my });
    if (dragging) { const { x, y } = toCanvas(e.clientX, e.clientY); setPositions(p => ({ ...p, [dragging.id]: { x: x - dragging.ox, y: y - dragging.oy } })); }
    if (connectSrc) { const r = canvasRef.current?.getBoundingClientRect(); if (r) setCursorPos({ x: e.clientX - r.left, y: e.clientY - r.top }); }
  };
  const onCanvasPointerUp = async e => {
    if (dragging) {
      const pos = positions[dragging.id];
      if (pos && wsId) { try { await api.put(`/workspaces/${wsId}/events/${dragging.id}`, { position_x: Math.round(pos.x), position_y: Math.round(pos.y) }); } catch {} }
      setDragging(null);
    }
    if (connectSrc) {
      const target = document.elementFromPoint(e.clientX, e.clientY);
      const card = target?.closest("[data-node-card]");
      if (card) {
        const targetId = card.dataset.nodeId;
        if (targetId && targetId !== connectSrc.event_id) {
          try {
            const { data } = await api.post(`/workspaces/${wsId}/edges`, { source: connectSrc.event_id, target: targetId, automation_id: automationId });
            setEdges(ed => [...ed, data]);
            toast.success("Connected");
          } catch { toast.error("Failed to connect"); }
        }
      }
      setConnectSrc(null); setCursorPos(null);
    }
    panStart.current = null;
  };
  const onWheel = e => {
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) {
      const r = canvasRef.current?.getBoundingClientRect();
      const mx = (e.clientX - r.left - pan.x) / scale, my = (e.clientY - r.top - pan.y) / scale;
      const ns = Math.min(3, Math.max(0.2, scale * (e.deltaY > 0 ? 0.92 : 1.08)));
      setPan({ x: e.clientX - r.left - mx * ns, y: e.clientY - r.top - my * ns }); setScale(ns);
    } else { setPan(p => ({ x: p.x - e.deltaX, y: p.y - e.deltaY })); }
  };

  const addNode = async (type) => {
    if (!wsId) return;
    const cw = canvasRef.current?.clientWidth || 800, ch = canvasRef.current?.clientHeight || 600;
    const x = (cw / 2 - pan.x) / scale, y = (ch / 2 - pan.y) / scale;
    const now = new Date().toISOString();
    try {
      const { data } = await api.post(`/workspaces/${wsId}/events`, {
        title: type.charAt(0).toUpperCase() + type.slice(1), type, start: now, end: now,
        position_x: x, position_y: y, automation_id: automationId,
      });
      setEvents(ev => { const next = [...ev, data]; onNodeCountChange?.(automationId, next.length); return next; });
      setPositions(p => ({ ...p, [data.event_id]: { x, y } }));
    } catch { toast.error("Failed to add node"); }
  };

  const saveNode = async (id, patch) => {
    try { await api.put(`/workspaces/${wsId}/events/${id}`, patch); setEvents(ev => ev.map(e => e.event_id === id ? { ...e, ...patch } : e)); }
    catch { toast.error("Failed to save"); }
  };

  const deleteNode = async (id) => {
    try {
      await api.delete(`/workspaces/${wsId}/events/${id}`);
      setEvents(ev => { const next = ev.filter(e => e.event_id !== id); onNodeCountChange?.(automationId, next.length); return next; });
      setEdges(ed => ed.filter(e => e.source !== id && e.target !== id));
    } catch {}
  };

  const deleteEdge = async (edgeId) => {
    try { await api.delete(`/workspaces/${wsId}/edges/${edgeId}`); setEdges(ed => ed.filter(e => e.edge_id !== edgeId)); } catch {}
  };

  const toggleComplete = async (node) => {
    const next = node.completed_at ? null : new Date().toISOString();
    setEvents(ev => ev.map(e => e.event_id === node.event_id ? { ...e, completed_at: next } : e));
    try {
      await api.put(`/workspaces/${wsId}/events/${node.event_id}`, { completed_at: next });
      if (next) {
        const dependents = events.filter(e => Array.isArray(e.depends_on) && e.depends_on.includes(node.event_id) && e.status === "blocked");
        for (const dep of dependents) {
          const allDone = dep.depends_on.every(depId => depId === node.event_id || events.find(e => e.event_id === depId)?.completed_at);
          if (allDone) {
            setEvents(ev => ev.map(e => e.event_id === dep.event_id ? { ...e, status: "todo" } : e));
            api.put(`/workspaces/${wsId}/events/${dep.event_id}`, { status: "todo" }).catch(() => {});
            toast(`"${dep.title}" unlocked`, { icon: "🔓" });
          }
        }
      }
    } catch {}
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div ref={canvasRef} className="flex-1 relative overflow-hidden select-none"
        style={{ background: "#4B4B50", backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.35) 1.5px, transparent 1.5px)", backgroundSize: `${28*scale}px ${28*scale}px`, backgroundPosition: `${pan.x%(28*scale)}px ${pan.y%(28*scale)}px` }}
        onPointerDown={onCanvasPointerDown} onPointerMove={onCanvasPointerMove} onPointerUp={onCanvasPointerUp} onWheel={onWheel}>

        <svg className="absolute inset-0 pointer-events-none" style={{ width: "100%", height: "100%", overflow: "visible" }}>
          <g transform={`translate(${pan.x},${pan.y}) scale(${scale})`}>
            {edges.map(edge => {
              const sp = positions[edge.source], tp = positions[edge.target];
              if (!sp || !tp) return null;
              const sx = sp.x + NODE_W, sy = sp.y + NODE_H / 2, tx = tp.x, ty = tp.y + NODE_H / 2, cx = (sx + tx) / 2;
              const srcEv = events.find(e => e.event_id === edge.source);
              const tgtEv = events.find(e => e.event_id === edge.target);
              const colorA = NODE_TYPE_COLORS[srcEv?.type] || "#5A90F5";
              const colorB = NODE_TYPE_COLORS[tgtEv?.type] || "#5A90F5";
              const gradId = `grad-${edge.edge_id}`;
              return (
                <g key={edge.edge_id} className="pointer-events-auto cursor-pointer" onClick={() => deleteEdge(edge.edge_id)}>
                  <defs>
                    <linearGradient id={gradId} x1={sx} y1={sy} x2={tx} y2={ty} gradientUnits="userSpaceOnUse">
                      <stop offset="0%" stopColor={colorA} /><stop offset="100%" stopColor={colorB} />
                    </linearGradient>
                  </defs>
                  <path d={`M${sx},${sy} C${cx},${sy} ${cx},${ty} ${tx},${ty}`} fill="none" stroke={`url(#${gradId})`} strokeWidth={2.5} strokeOpacity={0.85} strokeLinecap="round" />
                  <circle cx={tx} cy={ty} r={4} fill={colorB} opacity={0.95} />
                </g>
              );
            })}
          </g>
        </svg>
        {connectSrc && cursorPos && positions[connectSrc.event_id] && (
          <svg className="absolute inset-0 pointer-events-none" style={{ width: "100%", height: "100%" }}>
            {(() => {
              const sp = positions[connectSrc.event_id];
              const sx = (sp.x + NODE_W) * scale + pan.x, sy = (sp.y + NODE_H/2) * scale + pan.y;
              const color = NODE_TYPE_COLORS[connectSrc.type] || "#5A90F5";
              return <path d={`M${sx},${sy} C${(sx+cursorPos.x)/2},${sy} ${(sx+cursorPos.x)/2},${cursorPos.y} ${cursorPos.x},${cursorPos.y}`} fill="none" stroke={color} strokeWidth={2} strokeDasharray="4 4" strokeLinecap="round" />;
            })()}
          </svg>
        )}

        <div style={{ transform: `translate(${pan.x}px,${pan.y}px) scale(${scale})`, transformOrigin: "0 0", position: "absolute", inset: 0, pointerEvents: "none" }}>
          {events.map(ev => {
            const pos = positions[ev.event_id]; if (!pos) return null;
            const color = NODE_TYPE_COLORS[ev.type] || "#5A90F5";
            const locked = isLocked(ev);
            const done = !!ev.completed_at;
            return (
              <div key={ev.event_id} data-node-card data-node-id={ev.event_id}
                className="absolute rounded-2xl border transition-shadow"
                style={{ left: pos.x, top: pos.y, width: NODE_W, height: NODE_H, background: "#161616", borderColor: locked ? "#3A3A3A" : color + "55", borderWidth: 1.5, pointerEvents: "auto", opacity: locked ? 0.6 : 1, boxShadow: `0 4px 18px rgba(0,0,0,0.35)` }}
                onPointerDown={e => {
                  if (e.target.closest("[data-conn-port]")) return;
                  e.stopPropagation();
                  const { x, y } = toCanvas(e.clientX, e.clientY);
                  setDragging({ id: ev.event_id, ox: x - pos.x, oy: y - pos.y });
                  e.currentTarget.setPointerCapture(e.pointerId);
                }}
                onDoubleClick={() => setEditingNode(ev)}
                onContextMenu={e => { e.preventDefault(); e.stopPropagation(); setCtxMenu({ x: e.clientX, y: e.clientY, node: ev }); }}>
                <div className="h-1 w-full rounded-t-2xl" style={{ background: locked ? "#3A3A3A" : color }} />
                <div className="px-3.5 py-2 flex flex-col justify-center" style={{ height: "calc(100% - 4px)" }}>
                  <div className="flex items-center gap-2">
                    <button onPointerDown={e => e.stopPropagation()} onClick={() => !locked && toggleComplete(ev)}
                      className="flex-shrink-0" disabled={locked} title={locked ? "Locked — waiting on dependencies" : done ? "Mark incomplete" : "Mark complete"}>
                      {locked ? <Lock className="w-3.5 h-3.5 text-[#71717A]" /> : done ? <Check className="w-3.5 h-3.5" style={{ color }} /> : <div className="w-3.5 h-3.5 rounded-full border-2" style={{ borderColor: color }} />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <p className="text-[12px] font-semibold truncate" style={{ color: locked ? "#71717A" : "#EDEDED", textDecoration: done ? "line-through" : "none" }}>{ev.title || ev.type}</p>
                      <p className="text-[9px] capitalize" style={{ color: locked ? "#52525B" : "#A1A1AA" }}>{ev.type}{locked && " · locked"}</p>
                    </div>
                  </div>
                  {Array.isArray(ev.depends_on) && ev.depends_on.length > 0 && (
                    <p className="text-[8px] text-[#71717A] mt-1 ml-5.5 pl-0.5">{ev.depends_on.filter(id => events.find(e=>e.event_id===id)?.completed_at).length}/{ev.depends_on.length} deps done</p>
                  )}
                </div>
                <div data-conn-port className="absolute cursor-crosshair"
                  style={{ right: -8, top: "50%", transform: "translateY(-50%)", width: 16, height: 16, borderRadius: "50%", background: color, border: "2px solid #161616", zIndex: 10 }}
                  onPointerDown={e => { e.stopPropagation(); e.preventDefault(); e.currentTarget.setPointerCapture(e.pointerId); setConnectSrc(ev); const r = canvasRef.current?.getBoundingClientRect(); if (r) setCursorPos({ x: e.clientX - r.left, y: e.clientY - r.top }); }} />
              </div>
            );
          })}
        </div>

        <div className="absolute bottom-4 left-4 flex gap-1.5">
          <button onClick={() => setScale(s => Math.min(3, s * 1.2))} className="w-7 h-7 rounded-lg bg-[#1A1A1A]/90 border border-[#2A2A2A] text-[#A1A1AA] hover:text-white text-sm flex items-center justify-center backdrop-blur"><Plus className="w-3.5 h-3.5" /></button>
          <button onClick={() => setScale(s => Math.max(0.2, s * 0.8))} className="w-7 h-7 rounded-lg bg-[#1A1A1A]/90 border border-[#2A2A2A] text-[#A1A1AA] hover:text-white text-sm flex items-center justify-center backdrop-blur"><Minus className="w-3.5 h-3.5" /></button>
          <button onClick={() => { setPan({ x: 40, y: 40 }); setScale(0.85); }} className="px-2 h-7 rounded-lg bg-[#1A1A1A]/90 border border-[#2A2A2A] text-[10px] text-[#A1A1AA] hover:text-white backdrop-blur">Reset</button>
          <span className="px-2 h-7 flex items-center text-[10px] text-[#EDEDED] bg-[#1A1A1A]/70 rounded-lg backdrop-blur">{Math.round(scale * 100)}%</span>
        </div>

        <div className="absolute bottom-4 right-4 flex flex-col gap-1.5">
          {ADD_TYPES.map(({ type, icon: Icon, color }) => (
            <button key={type} onClick={() => addNode(type)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold border capitalize transition-colors backdrop-blur"
              style={{ background: color + "30", borderColor: color + "70", color: "#fff" }}>
              <Icon className="w-3 h-3" /> {type}
            </button>
          ))}
        </div>

        {events.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <p className="text-white/70 text-sm mb-1 font-medium">{automation.name}</p>
              <p className="text-white/40 text-xs">Add nodes with the buttons → drag from the dot to connect</p>
            </div>
          </div>
        )}
      </div>

      {ctxMenu && (
        <>
          <div className="fixed inset-0 z-[400]" onClick={() => setCtxMenu(null)} />
          <div className="fixed z-[401] bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl shadow-2xl p-1 w-44 text-xs"
            style={{ left: ctxMenu.x, top: ctxMenu.y }}>
            <button onClick={() => { setEditingNode(ctxMenu.node); setCtxMenu(null); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#2A2A2A] text-[#EDEDED]">
              <Edit3 className="w-3.5 h-3.5" /> Edit
            </button>
            <button onClick={() => { if (!isLocked(ctxMenu.node)) toggleComplete(ctxMenu.node); setCtxMenu(null); }} disabled={isLocked(ctxMenu.node)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#2A2A2A] text-[#EDEDED] disabled:opacity-30">
              {ctxMenu.node.completed_at ? <><Unlock className="w-3.5 h-3.5" /> Mark incomplete</> : <><Check className="w-3.5 h-3.5" /> Mark complete</>}
            </button>
            <div className="h-px bg-[#2A2A2A] my-1" />
            <button onClick={() => { deleteNode(ctxMenu.node.event_id); setCtxMenu(null); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#EF4444]/10 text-[#EF4444]">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          </div>
        </>
      )}

      {editingNode && <NodeDialog node={editingNode} allNodes={events} onClose={() => setEditingNode(null)} onSave={saveNode} onDelete={deleteNode} />}
    </div>
  );
}

// ─── Main Automations Page — list of separate automation boards ────────────
export default function Automations() {
  const { current } = useWorkspace();
  const wsId = current?.workspace_id;
  const [automations, setAutomations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAuto, setEditingAuto] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    if (!wsId) return;
    try {
      const { data } = await api.get(`/workspaces/${wsId}/automation-boards`, { params: { archived: showArchived } });
      setAutomations(data);
      if (!activeId && data.length > 0) setActiveId(data[0].automation_id);
    } catch {} finally { setLoading(false); }
  }, [wsId, showArchived]);

  useEffect(() => { load(); }, [wsId, showArchived]);

  const createAutomation = async (body) => {
    try {
      const { data } = await api.post(`/workspaces/${wsId}/automation-boards`, body);
      setAutomations(a => [data, ...a]);
      setActiveId(data.automation_id);
      setShowForm(false);
    } catch { toast.error("Failed to create automation"); }
  };

  const updateAutomation = async (body) => {
    try {
      const { data } = await api.put(`/workspaces/${wsId}/automation-boards/${editingAuto.automation_id}`, body);
      setAutomations(a => a.map(x => x.automation_id === data.automation_id ? data : x));
      setEditingAuto(null);
    } catch {}
  };

  const archiveAutomation = async (id) => {
    try {
      await api.put(`/workspaces/${wsId}/automation-boards/${id}`, { archived: true });
      setAutomations(a => a.filter(x => x.automation_id !== id));
      if (activeId === id) setActiveId(automations.find(x => x.automation_id !== id)?.automation_id || null);
    } catch {}
  };

  const deleteAutomation = async (id) => {
    if (!window.confirm("Delete this automation and all its nodes/connections?")) return;
    try {
      await api.delete(`/workspaces/${wsId}/automation-boards/${id}`);
      setAutomations(a => a.filter(x => x.automation_id !== id));
      if (activeId === id) setActiveId(automations.find(x => x.automation_id !== id)?.automation_id || null);
    } catch {}
  };

  const updateNodeCount = (id, count) => {
    setAutomations(a => a.map(x => x.automation_id === id ? { ...x, node_count: count } : x));
  };

  const active = automations.find(a => a.automation_id === activeId);

  return (
    <div className="flex h-full bg-[#0A0A0A] overflow-hidden">
      {/* Sidebar — list of automations */}
      <div className="w-56 flex-shrink-0 border-r border-[#1A1A1A] flex flex-col bg-[#090909]">
        <div className="px-4 py-4 border-b border-[#1A1A1A] flex items-center justify-between">
          <h2 className="text-[12px] font-semibold text-[#A1A1AA] uppercase tracking-wider">Automations</h2>
          <button onClick={() => setShowForm(true)} className="w-5 h-5 rounded-md bg-[#1a1a1a] hover:bg-[#2A2A2A] flex items-center justify-center text-[#A1A1AA] hover:text-[#EDEDED] transition-colors">
            <Plus className="w-3 h-3" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {loading ? (
            <div className="px-4 py-3 text-[11px] text-[#71717A]">Loading…</div>
          ) : automations.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <p className="text-[11px] text-[#71717A] mb-3">No automations yet</p>
              <button onClick={() => setShowForm(true)} className="text-[11px] text-[#5A90F5] hover:text-[#4A80E5]">Create first automation →</button>
            </div>
          ) : automations.map(a => {
            const isActive = a.automation_id === activeId;
            return (
              <div key={a.automation_id} className={`group mx-1 rounded-xl transition-all ${isActive ? "bg-[#141414]" : "hover:bg-[#0F0F0F]"}`}>
                <button onClick={() => setActiveId(a.automation_id)} className="w-full text-left px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: a.color }} />
                    <span className="text-[11px] font-medium truncate flex-1" style={{ color: isActive ? "#EDEDED" : "#A1A1AA" }}>{a.name}</span>
                  </div>
                  <p className="text-[9px] text-[#71717A] mt-1 pl-4">{a.node_count || 0} node{a.node_count !== 1 ? "s" : ""}</p>
                </button>
              </div>
            );
          })}
        </div>
        <div className="px-4 py-3 border-t border-[#1A1A1A]">
          <button onClick={() => setShowArchived(v => !v)} className="w-full text-[10px] text-[#71717A] hover:text-[#A1A1AA] flex items-center gap-1.5 transition-colors">
            <Archive className="w-3 h-3" /> {showArchived ? "Hide archived" : "Show archived"}
          </button>
        </div>
      </div>

      {/* Active automation canvas */}
      <div className="flex-1 min-w-0 flex flex-col">
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-[#111] border border-[#2A2A2A] flex items-center justify-center">
              <Zap className="w-7 h-7 text-[#71717A]" />
            </div>
            <div className="text-center">
              <p className="text-[15px] font-semibold text-[#71717A]">No automation selected</p>
              <p className="text-[12px] text-[#52525B] mt-1">Create one to start building dependency-driven logic</p>
            </div>
            <button onClick={() => setShowForm(true)} className="px-5 py-2.5 rounded-xl text-[12px] font-semibold bg-[#5A90F5] hover:bg-[#4A80E5] text-white transition-colors">
              Create first automation
            </button>
          </div>
        ) : (
          <>
            <div className="px-6 py-3 border-b border-[#1A1A1A] flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-2 h-2 rounded-full" style={{ background: active.color }} />
                <h1 className="text-[14px] font-semibold text-[#EDEDED]">{active.name}</h1>
                {active.description && <span className="text-[11px] text-[#71717A]">— {active.description}</span>}
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setEditingAuto(active)} className="p-1.5 rounded-lg hover:bg-[#1a1a1a] text-[#71717A] hover:text-[#A1A1AA]"><Edit3 className="w-3.5 h-3.5" /></button>
                <button onClick={() => archiveAutomation(active.automation_id)} className="p-1.5 rounded-lg hover:bg-[#1a1a1a] text-[#71717A] hover:text-[#A1A1AA]"><Archive className="w-3.5 h-3.5" /></button>
                <button onClick={() => deleteAutomation(active.automation_id)} className="p-1.5 rounded-lg hover:bg-[#EF4444]/10 text-[#71717A] hover:text-[#EF4444]"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <AutomationCanvas key={active.automation_id} automation={active} wsId={wsId} onNodeCountChange={updateNodeCount} />
          </>
        )}
      </div>

      {showForm && <AutomationForm onClose={() => setShowForm(false)} onSave={createAutomation} />}
      {editingAuto && <AutomationForm initial={editingAuto} onClose={() => setEditingAuto(null)} onSave={updateAutomation} />}
    </div>
  );
}
